import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logsQueryOptions } from '../../api/admin';
import { adminControllerGetLogs } from '../../api/generated';
import { downloadBlob } from '../../utils/downloadBlob';
import { LoadingSpinner } from './shared';
import type { AuditLog } from './types';
import { detailArr, detailNum, detailStr, fmt } from './utils';

function renderTarget(log: AuditLog, t: (key: string) => string) {
  const d = log.details;

  if (log.action === 'photo.upload' || log.action === 'photo.delete') {
    const upn = detailStr(d, 'targetUpn');
    const name = detailStr(d, 'targetName');
    if (upn || name) {
      return (
        <span>
          {name && <span className="font-medium">{name}</span>}
          {upn && (
            <span className="mono block text-[11px] text-muted">{upn}</span>
          )}
          {log.action === 'photo.upload' && detailNum(d, 'sizeBytes') > 0 && (
            <span className="block text-[11px] text-muted-soft">
              {Math.round(detailNum(d, 'sizeBytes') / 1024)} KB
            </span>
          )}
        </span>
      );
    }
  }

  if (log.action === 'batch.upload') {
    const targets = detailArr(d, 'targets');
    const errorCount = detailNum(d, 'error');
    const notFoundCount = detailNum(d, 'notFound');
    return (
      <span>
        <span className="text-ink-soft">
          {detailNum(d, 'total') || '?'} files —{' '}
          <span className="text-green-ink">
            {detailNum(d, 'success')} {t('admin.logs.statusOk')}
          </span>
          {errorCount > 0 && (
            <span className="text-magenta">
              {' '}
              · {errorCount} {t('admin.logs.err')}
            </span>
          )}
          {notFoundCount > 0 && (
            <span className="text-coral">
              {' '}
              · {notFoundCount} {t('admin.logs.notFound')}
            </span>
          )}
        </span>
        {targets.length > 0 && (
          <span className="mono block max-w-[220px] truncate text-[11px] text-muted-soft">
            {targets
              .filter(
                (item): item is Record<string, unknown> =>
                  typeof item === 'object' && item !== null,
              )
              .map((item) => (typeof item.login === 'string' ? item.login : ''))
              .filter(Boolean)
              .join(', ')}
          </span>
        )}
      </span>
    );
  }

  if (log.action === 'card.generate') {
    const hasVerso = !!d?.hasVerso;
    const outputFormat = detailStr(d, 'outputFormat');
    const csvFileName = detailStr(d, 'csvFileName');
    const templateId = detailStr(d, 'templateId');
    return (
      <span>
        <span className="font-medium text-ink-soft">
          {detailStr(d, 'templateName') ?? '—'}
          {hasVerso && (
            <span className="text-muted"> + {t('admin.templates.verso')}</span>
          )}
        </span>
        {templateId && (
          <span className="mono block max-w-[220px] truncate text-[11px] text-muted-soft">
            {templateId}
          </span>
        )}
        <span className="block text-[11px] text-muted">
          {detailNum(d, 'rows') || '?'} {t('admin.logs.rows')}
          {outputFormat && (
            <span className="mono ml-2 uppercase">{outputFormat}</span>
          )}
        </span>
        {csvFileName && (
          <span className="mono block max-w-[220px] truncate text-[11px] text-muted-soft">
            {csvFileName}
          </span>
        )}
        {!!d?.deduplicated && (
          <span className="block text-coral">
            {t('admin.logs.deduplicated')}
          </span>
        )}
      </span>
    );
  }

  if (log.action === 'admin.user.update') {
    const upn = detailStr(d, 'targetUpn');
    const name = detailStr(d, 'targetName');
    const changes: string[] = [];
    const role = detailStr(d, 'role');
    if (role !== null) {
      changes.push(`role → ${role}`);
    }
    if (d?.active !== undefined) {
      changes.push(`active → ${d.active === true ? 'true' : 'false'}`);
    }
    return (
      <span>
        {name && <span className="font-medium">{name}</span>}
        {upn && (
          <span className="mono block text-[11px] text-muted">{upn}</span>
        )}
        {changes.length > 0 && (
          <span className="block text-[11px] text-muted">
            {changes.join(', ')}
          </span>
        )}
      </span>
    );
  }

  if (log.action.startsWith('admin.template.')) {
    const name = detailStr(d, 'templateName');
    const active = d?.active;
    return (
      <span>
        {name && <span className="font-medium">{name}</span>}
        {active !== undefined && (
          <span className="block text-[11px] text-muted">
            active → {active === true ? 'true' : 'false'}
          </span>
        )}
      </span>
    );
  }

  if (log.targetId) {
    return (
      <span className="mono truncate text-[11px] text-muted">
        {log.targetId}
      </span>
    );
  }

  return <span className="text-muted-soft">—</span>;
}

function actionBadge(action: string) {
  const verb = action.split('.').pop() ?? '';
  let cls: string;
  switch (verb) {
    case 'create':
      cls = 'epi-tag green';
      break;
    case 'delete':
      cls = 'epi-tag magenta';
      break;
    case 'update':
    case 'rename':
      cls = 'epi-tag blue';
      break;
    case 'upload':
      cls = 'epi-tag';
      break;
    case 'generate':
      cls = 'epi-tag coral';
      break;
    default:
      cls = 'epi-tag muted';
  }
  return <span className={cls}>{action}</span>;
}

function exportCsv(logs: AuditLog[]) {
  const headers = [
    'id',
    'date',
    'action',
    'actor',
    'actorUpn',
    'actorOid',
    'targetId',
    'details',
    'ip',
  ];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = logs.map((l) =>
    [
      String(l.id),
      new Date(l.createdAt).toISOString(),
      l.action,
      l.actorName ?? '',
      l.actorUpn ?? '',
      l.actorOid,
      l.targetId ?? '',
      l.details ? JSON.stringify(l.details) : '',
      l.ip ?? '',
    ]
      .map(escape)
      .join(','),
  );
  const csv = [headers.join(','), ...rows].join('\n');
  downloadBlob(
    new Blob([csv], { type: 'text/csv' }),
    `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

/** The audit-log filter fields; every one is an optional free-text/date entry. */
interface LogFilters {
  action: string;
  actorOid: string;
  targetUpn: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: LogFilters = {
  action: '',
  actorOid: '',
  targetUpn: '',
  from: '',
  to: '',
};

type LogFilterForm = ReturnType<typeof useLogFilterForm>;

/** Typed shorthand for the filter form, so the field wrappers below can name it. */
/** Only the filled-in filters, typed — named one by one, since iterating
 *  `Object.keys` would erase the keys and force a cast. */
function compactFilters({
  action,
  actorOid,
  targetUpn,
  from,
  to,
}: LogFilters): Partial<LogFilters> {
  return {
    ...(action ? { action } : {}),
    ...(actorOid ? { actorOid } : {}),
    ...(targetUpn ? { targetUpn } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
}

function useLogFilterForm(onSubmit: (value: LogFilters) => void) {
  return useForm({
    defaultValues: EMPTY_FILTERS,
    onSubmit: ({ value }) => onSubmit(value),
  });
}

/**
 * The filters are the same input several times over. Each subscribes on its own,
 * so typing in one re-renders that field and not the log table beside it.
 */
function TextFilter({
  form,
  name,
  placeholder,
}: {
  form: LogFilterForm;
  name: 'action' | 'actorOid' | 'targetUpn';
  placeholder: string;
}) {
  return (
    <form.Field name={name}>
      {(field) => (
        <input
          placeholder={placeholder}
          value={field.state.value}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          className="min-w-36 flex-1 px-3 py-1.5 text-xs"
        />
      )}
    </form.Field>
  );
}

function DateFilter({
  form,
  name,
}: {
  form: LogFilterForm;
  name: 'from' | 'to';
}) {
  const { t } = useTranslation();
  return (
    <form.Field name={name}>
      {(field) => (
        <input
          type="date"
          aria-label={
            name === 'from' ? t('admin.logs.dateFrom') : t('admin.logs.dateTo')
          }
          value={field.state.value}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          className="px-3 py-1.5 text-xs"
        />
      )}
    </form.Field>
  );
}

export function LogsTab({
  page,
  onPageChange,
}: {
  page: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const [appliedFilters, setAppliedFilters] =
    useState<LogFilters>(EMPTY_FILTERS);
  const [expanded, setExpanded] = useState<number | null>(null);

  // The form holds what is typed; `appliedFilters` holds what the last submit
  // actually sent, so editing a field doesn't refetch until Search is pressed.
  const form = useLogFilterForm((value) => {
    setAppliedFilters(value);
    onPageChange(1);
    setExpanded(null);
  });

  const activeFilters = compactFilters(appliedFilters);

  const { data, isLoading: loading } = useQuery(
    logsQueryOptions({ page, limit: 50, ...activeFilters }),
  );

  const handleExport = async () => {
    try {
      const { data: result } = await adminControllerGetLogs({
        query: {
          page: 1,
          limit: 5000,
          action: appliedFilters.action || undefined,
          actorOid: appliedFilters.actorOid || undefined,
          targetUpn: appliedFilters.targetUpn || undefined,
          from: appliedFilters.from || undefined,
          to: appliedFilters.to || undefined,
        },
        throwOnError: true,
      });
      exportCsv(result.logs);
    } catch {
      /* noop */
    }
  };

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label dash">{t('admin.logs.title')}</p>
          {data && (
            <p className="mt-1 text-sm text-muted">
              {t('admin.logs.totalEntries', { count: data.total })}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            void handleExport();
          }}
          className="btn-ghost"
        >
          {t('admin.logs.exportCsv')} ↓
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-wrap gap-2"
      >
        <TextFilter
          form={form}
          name="action"
          placeholder={t('admin.logs.filterByAction')}
        />
        <TextFilter
          form={form}
          name="actorOid"
          placeholder={t('admin.logs.actorOid')}
        />
        <TextFilter
          form={form}
          name="targetUpn"
          placeholder={t('admin.logs.targetUpn')}
        />
        <DateFilter form={form} name="from" />
        <DateFilter form={form} name="to" />
        <button type="submit" className="btn-blue">
          {t('admin.logs.search')}
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {data?.logs.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-soft">
              {t('admin.logs.noLogs')}
            </p>
          ) : (
            <div className="overflow-x-auto border-y border-ink">
              <table className="epi-table">
                <thead>
                  <tr>
                    <th>{t('admin.logs.colDate')}</th>
                    <th>{t('admin.logs.colAction')}</th>
                    <th>{t('admin.logs.colActor')}</th>
                    <th>{t('admin.logs.colTarget')}</th>
                    <th>{t('admin.logs.colIp')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`cursor-pointer ${expanded === log.id ? 'expanded-row' : ''}`}
                        tabIndex={0}
                        aria-expanded={expanded === log.id}
                        onClick={() =>
                          setExpanded(expanded === log.id ? null : log.id)
                        }
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') {
                            return;
                          }
                          e.preventDefault();
                          setExpanded(expanded === log.id ? null : log.id);
                        }}
                      >
                        <td className="mono text-[11px] whitespace-nowrap text-muted">
                          {fmt(log.createdAt)}
                        </td>
                        <td>{actionBadge(log.action)}</td>
                        <td className="max-w-[160px] truncate font-medium">
                          {log.actorName ?? log.actorUpn ?? log.actorOid}
                        </td>
                        <td className="max-w-[220px]">
                          {renderTarget(log, t)}
                        </td>
                        <td className="mono text-[11px] whitespace-nowrap text-muted-soft">
                          <div className="flex items-center gap-2">
                            <span>{log.ip ?? '—'}</span>
                            <span
                              className="inline-block [transition:transform_150ms]"
                              style={{
                                transform:
                                  expanded === log.id
                                    ? 'rotate(180deg)'
                                    : 'rotate(0)',
                              }}
                            >
                              ▾
                            </span>
                          </div>
                        </td>
                      </tr>
                      {expanded === log.id && (
                        <tr className="detail-row">
                          <td colSpan={5}>
                            <div className="space-y-2 text-xs">
                              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-ink-soft">
                                <span>
                                  <span className="label mr-[6px]">
                                    {t('admin.logs.labelId')}
                                  </span>
                                  {log.id}
                                </span>
                                <span>
                                  <span className="label mr-[6px]">
                                    {t('admin.logs.labelActorOid')}
                                  </span>
                                  <span className="mono">{log.actorOid}</span>
                                </span>
                                {log.actorUpn && (
                                  <span>
                                    <span className="label mr-[6px]">
                                      {t('admin.logs.labelActorUpn')}
                                    </span>
                                    {log.actorUpn}
                                  </span>
                                )}
                                {log.targetId && (
                                  <span>
                                    <span className="label mr-[6px]">
                                      {t('admin.logs.labelTargetId')}
                                    </span>
                                    <span className="mono">{log.targetId}</span>
                                  </span>
                                )}
                              </div>
                              {log.details && (
                                <pre className="mono overflow-x-auto border border-rule bg-bg-soft px-3 py-2 text-[11px] leading-relaxed text-ink-soft">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => onPageChange(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="btn-link"
                style={{ opacity: page === 1 ? 0.4 : 1 }}
              >
                {t('admin.logs.prev')}
              </button>
              <span className="label">
                {t('admin.logs.page', { page, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="btn-link"
                style={{ opacity: page === totalPages ? 0.4 : 1 }}
              >
                {t('admin.logs.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
